import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Grid, Plus, Edit, Trash2, Eye, EyeOff, 
  Loader2, Save, X, ListOrdered, ArrowLeft, DollarSign
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceManagement = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Product management state
  const [managingService, setManagingService] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productUploading, setProductUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: '', name_pt: '', description: '', icon: 'Utensils',
    route: '/restaurants', color: '#FF6B35', image: '',
    is_active: true, show_on_home: true, is_available: true, sort_order: 0
  });

  const [productForm, setProductForm] = useState({
    name: '', name_pt: '', description: '', description_pt: '',
    category: '', price: 0, image: '', is_available: true, is_popular: false
  });

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/service-categories`);
      if (response.ok) setServices(await response.json());
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (serviceId) => {
    setProductsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/services/${serviceId}/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setProducts(await response.json());
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleImageUpload = async (file, isProduct = false) => {
    if (!file) return;
    const setter = isProduct ? setProductUploading : setUploading;
    setter(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (response.ok) {
        const data = await response.json();
        if (isProduct) {
          setProductForm(prev => ({ ...prev, image: data.url }));
        } else {
          setFormData(prev => ({ ...prev, image: data.url }));
        }
        toast.success(language === 'pt' ? 'Imagem carregada!' : 'Image uploaded!');
      }
    } catch {
      toast.error(language === 'pt' ? 'Erro ao carregar imagem' : 'Error uploading image');
    } finally {
      setter(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.name_pt) {
      toast.error(language === 'pt' ? 'Preencha o nome em ambos os idiomas' : 'Fill name in both languages');
      return;
    }
    try {
      const url = editingService 
        ? `${API_URL}/api/admin/service-categories/${editingService.category_id}`
        : `${API_URL}/api/admin/service-categories`;
      const response = await fetch(url, {
        method: editingService ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        toast.success(language === 'pt' ? 'Serviço guardado!' : 'Service saved!');
        setShowDialog(false);
        resetForm();
        fetchServices();
      } else {
        toast.error(language === 'pt' ? 'Erro ao guardar' : 'Error saving');
      }
    } catch {
      toast.error(language === 'pt' ? 'Erro ao guardar' : 'Error saving');
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm(language === 'pt' ? 'Tem certeza?' : 'Are you sure?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/service-categories/${serviceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success(language === 'pt' ? 'Serviço excluído!' : 'Service deleted!');
        fetchServices();
      }
    } catch {
      toast.error(language === 'pt' ? 'Erro ao excluir' : 'Error deleting');
    }
  };

  const toggleVisibility = async (service, field) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/service-categories/${service.category_id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: !service[field] })
      });
      if (response.ok) { toast.success(language === 'pt' ? 'Atualizado!' : 'Updated!'); fetchServices(); }
    } catch {
      toast.error(language === 'pt' ? 'Erro ao atualizar' : 'Error updating');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', name_pt: '', description: '', icon: 'Utensils', route: '/restaurants', color: '#FF6B35', image: '', is_active: true, show_on_home: true, is_available: true, sort_order: 0 });
    setEditingService(null);
  };

  const openEditDialog = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name || '', name_pt: service.name_pt || service.name || '',
      description: service.description || '', icon: service.icon || 'Utensils',
      route: service.route || '/', color: service.color || '#FF6B35',
      image: service.image || '', is_active: service.is_active !== false,
      show_on_home: service.show_on_home !== false, is_available: service.is_available !== false,
      sort_order: service.sort_order || 0
    });
    setShowDialog(true);
  };

  // ====== PRODUCT MANAGEMENT ======

  const openProductManager = (service) => {
    setManagingService(service);
    fetchProducts(service.category_id);
  };

  const closeProductManager = () => {
    setManagingService(null);
    setProducts([]);
  };

  const resetProductForm = () => {
    setProductForm({ name: '', name_pt: '', description: '', description_pt: '', category: '', price: 0, image: '', is_available: true, is_popular: false });
    setEditingProduct(null);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '', name_pt: product.name_pt || '',
      description: product.description || '', description_pt: product.description_pt || '',
      category: product.category || '', price: product.price || 0,
      image: product.image || '', is_available: product.is_available !== false,
      is_popular: product.is_popular === true
    });
    setShowProductDialog(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.price) {
      toast.error(language === 'pt' ? 'Preencha o nome e o preço' : 'Fill name and price');
      return;
    }
    try {
      let url, method;
      if (editingProduct) {
        url = `${API_URL}/api/admin/services/products/${editingProduct.product_id}`;
        method = 'PUT';
      } else {
        url = `${API_URL}/api/admin/services/${managingService.category_id}/products`;
        method = 'POST';
      }
      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(productForm)
      });
      if (response.ok) {
        toast.success(language === 'pt' ? 'Produto guardado!' : 'Product saved!');
        setShowProductDialog(false);
        resetProductForm();
        fetchProducts(managingService.category_id);
      } else {
        toast.error(language === 'pt' ? 'Erro ao guardar produto' : 'Error saving product');
      }
    } catch {
      toast.error(language === 'pt' ? 'Erro ao guardar produto' : 'Error saving product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm(language === 'pt' ? 'Eliminar este produto?' : 'Delete this product?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/services/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success(language === 'pt' ? 'Produto eliminado!' : 'Product deleted!');
        fetchProducts(managingService.category_id);
      }
    } catch {
      toast.error(language === 'pt' ? 'Erro ao eliminar' : 'Error deleting');
    }
  };

  const toggleProductField = async (product, field) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/services/products/${product.product_id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: !product[field] })
      });
      if (response.ok) {
        toast.success(language === 'pt' ? 'Atualizado!' : 'Updated!');
        fetchProducts(managingService.category_id);
      }
    } catch {
      toast.error(language === 'pt' ? 'Erro' : 'Error');
    }
  };

  const iconOptions = ['Utensils', 'Wind', 'ShoppingCart', 'Pill', 'Package', 'Coffee', 'Wine', 'Snowflake', 'Sun', 'Umbrella'];

  // ====== PRODUCT MANAGEMENT VIEW ======
  if (managingService) {
    return (
      <Card className="rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={closeProductManager} data-testid="back-to-services-btn">
                <ArrowLeft size={16} />
              </Button>
              <ListOrdered className="text-[#FF6B35]" size={20} />
              <span>
                {language === 'pt' ? 'Menu de' : 'Menu for'}{' '}
                <span className="text-[#FF6B35]">{language === 'pt' ? (managingService.name_pt || managingService.name) : managingService.name}</span>
              </span>
            </span>
            <Dialog open={showProductDialog} onOpenChange={(open) => { setShowProductDialog(open); if (!open) resetProductForm(); }}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full text-xs" data-testid="add-product-btn">
                  <Plus size={14} className="mr-1" />
                  {language === 'pt' ? 'Novo Produto' : 'New Product'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingProduct 
                      ? (language === 'pt' ? 'Editar Produto' : 'Edit Product')
                      : (language === 'pt' ? 'Novo Produto' : 'New Product')}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{language === 'pt' ? 'Nome (EN)' : 'Name (EN)'} *</Label>
                      <Input value={productForm.name} onChange={(e) => setProductForm(prev => ({...prev, name: e.target.value}))} className="mt-1" data-testid="product-name-input" />
                    </div>
                    <div>
                      <Label>{language === 'pt' ? 'Nome (PT)' : 'Name (PT)'}</Label>
                      <Input value={productForm.name_pt} onChange={(e) => setProductForm(prev => ({...prev, name_pt: e.target.value}))} className="mt-1" data-testid="product-name-pt-input" />
                    </div>
                  </div>
                  <div>
                    <Label>{language === 'pt' ? 'Descrição' : 'Description'}</Label>
                    <Textarea value={productForm.description} onChange={(e) => setProductForm(prev => ({...prev, description: e.target.value}))} className="mt-1" rows={2} data-testid="product-description-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{language === 'pt' ? 'Categoria' : 'Category'}</Label>
                      <Input value={productForm.category} onChange={(e) => setProductForm(prev => ({...prev, category: e.target.value}))} className="mt-1" placeholder="ex: Entradas, Pratos" data-testid="product-category-input" />
                    </div>
                    <div>
                      <Label>{language === 'pt' ? 'Preço (EUR)' : 'Price (EUR)'} *</Label>
                      <Input type="number" step="0.01" min="0" value={productForm.price} onChange={(e) => setProductForm(prev => ({...prev, price: parseFloat(e.target.value) || 0}))} className="mt-1" data-testid="product-price-input" />
                    </div>
                  </div>
                  <div>
                    <Label>{language === 'pt' ? 'Imagem' : 'Image'}</Label>
                    <div className="mt-1 space-y-2">
                      {productForm.image && (
                        <div className="relative w-full h-24 bg-gray-100 rounded-lg overflow-hidden">
                          <img src={productForm.image.startsWith('http') ? productForm.image : `${API_URL}${productForm.image}`} alt="Preview" className="w-full h-full object-cover" />
                          <button onClick={() => setProductForm(prev => ({...prev, image: ''}))} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5"><X size={10} /></button>
                        </div>
                      )}
                      <Input type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files?.[0], true)} disabled={productUploading} />
                      {productUploading && <Loader2 className="animate-spin" size={16} />}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{language === 'pt' ? 'Disponível' : 'Available'}</Label>
                      <Switch checked={productForm.is_available} onCheckedChange={(c) => setProductForm(prev => ({...prev, is_available: c}))} data-testid="product-available-switch" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>{language === 'pt' ? 'Popular / Destaque' : 'Popular / Featured'}</Label>
                      <Switch checked={productForm.is_popular} onCheckedChange={(c) => setProductForm(prev => ({...prev, is_popular: c}))} data-testid="product-popular-switch" />
                    </div>
                  </div>
                  <Button onClick={handleSaveProduct} className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full" data-testid="save-product-btn">
                    <Save size={16} className="mr-2" />
                    {language === 'pt' ? 'Guardar Produto' : 'Save Product'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {productsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#FF6B35]" /></div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <ListOrdered size={40} className="mx-auto text-[#9FA2B4] mb-3" />
              <p className="text-[#9FA2B4] text-sm">
                {language === 'pt' ? 'Nenhum produto neste serviço. Clique em "Novo Produto" para adicionar.' : 'No products in this service. Click "New Product" to add.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Group by category */}
              {(() => {
                const categories = [...new Set(products.map(p => p.category || 'Geral'))];
                return categories.map(cat => (
                  <div key={cat}>
                    <p className="text-xs font-semibold text-[#004E64] uppercase tracking-wide mt-3 mb-1.5 px-1">{cat}</p>
                    {products.filter(p => (p.category || 'Geral') === cat).map(product => (
                      <div key={product.product_id} className={`flex items-center justify-between p-3 rounded-xl border ${product.is_available ? 'bg-white border-[#004E64]/10' : 'bg-gray-50 border-gray-200'}`} data-testid={`product-item-${product.product_id}`}>
                        <div className="flex items-center gap-3">
                          {product.image ? (
                            <img src={product.image.startsWith('http') ? product.image : `${API_URL}${product.image}`} alt={product.name} className="w-12 h-12 object-cover rounded-lg" />
                          ) : (
                            <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                              <DollarSign size={18} className="text-[#FF6B35]" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-sm text-[#004E64]">
                              {language === 'pt' ? (product.name_pt || product.name) : product.name}
                              {product.is_popular && <Badge className="ml-2 bg-yellow-100 text-yellow-700 text-[10px]">Popular</Badge>}
                            </p>
                            {product.description && <p className="text-xs text-[#9FA2B4] line-clamp-1">{product.description}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-[#004E64] mr-1">{product.price.toFixed(2)}€</span>
                          {!product.is_available && <Badge className="bg-red-100 text-red-700 text-[10px]">{language === 'pt' ? 'Indisponível' : 'Unavailable'}</Badge>}
                          <Button variant="ghost" size="sm" onClick={() => toggleProductField(product, 'is_available')} title={product.is_available ? 'Desativar' : 'Ativar'}>
                            {product.is_available ? <Eye size={14} /> : <EyeOff size={14} />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditProduct(product)} data-testid={`edit-product-${product.product_id}`}><Edit size={14} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteProduct(product.product_id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // ====== SERVICE LIST VIEW ======
  return (
    <Card className="rounded-2xl">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Grid className="text-[#FF6B35]" size={20} />
            {language === 'pt' ? 'Gestão de Serviços' : 'Service Management'}
          </span>
          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full text-xs" data-testid="new-service-btn">
                <Plus size={14} className="mr-1" />
                {language === 'pt' ? 'Novo Serviço' : 'New Service'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingService 
                    ? (language === 'pt' ? 'Editar Serviço' : 'Edit Service')
                    : (language === 'pt' ? 'Novo Serviço' : 'New Service')}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{language === 'pt' ? 'Nome (EN)' : 'Name (EN)'} *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))} className="mt-1" placeholder="Restaurants" data-testid="service-name-input" />
                  </div>
                  <div>
                    <Label>{language === 'pt' ? 'Nome (PT)' : 'Name (PT)'} *</Label>
                    <Input value={formData.name_pt} onChange={(e) => setFormData(prev => ({...prev, name_pt: e.target.value}))} className="mt-1" placeholder="Restaurantes" data-testid="service-name-pt-input" />
                  </div>
                </div>
                <div>
                  <Label>{language === 'pt' ? 'Descrição' : 'Description'}</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))} className="mt-1" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{language === 'pt' ? 'Ícone' : 'Icon'}</Label>
                    <select value={formData.icon} onChange={(e) => setFormData(prev => ({...prev, icon: e.target.value}))} className="w-full mt-1 p-2 border rounded-lg">
                      {iconOptions.map(icon => (<option key={icon} value={icon}>{icon}</option>))}
                    </select>
                  </div>
                  <div>
                    <Label>{language === 'pt' ? 'Cor' : 'Color'}</Label>
                    <div className="flex gap-2 mt-1">
                      <input type="color" value={formData.color} onChange={(e) => setFormData(prev => ({...prev, color: e.target.value}))} className="w-12 h-10 rounded cursor-pointer" />
                      <Input value={formData.color} onChange={(e) => setFormData(prev => ({...prev, color: e.target.value}))} className="flex-1" />
                    </div>
                  </div>
                </div>
                <div>
                  <Label>{language === 'pt' ? 'Rota/URL' : 'Route/URL'}</Label>
                  <Input value={formData.route} onChange={(e) => setFormData(prev => ({...prev, route: e.target.value}))} className="mt-1" placeholder="/restaurants" />
                </div>
                <div>
                  <Label>{language === 'pt' ? 'Imagem' : 'Image'}</Label>
                  <div className="mt-1 space-y-2">
                    {formData.image && (
                      <div className="relative w-full h-32 bg-gray-100 rounded-lg overflow-hidden">
                        <img src={formData.image.startsWith('http') ? formData.image : `${API_URL}${formData.image}`} alt="Preview" className="w-full h-full object-cover" />
                        <button onClick={() => setFormData(prev => ({...prev, image: ''}))} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"><X size={12} /></button>
                      </div>
                    )}
                    <Input type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files?.[0])} disabled={uploading} />
                    {uploading && <Loader2 className="animate-spin" size={16} />}
                  </div>
                </div>
                <div>
                  <Label>{language === 'pt' ? 'Ordem de Exibição' : 'Sort Order'}</Label>
                  <Input type="number" value={formData.sort_order} onChange={(e) => setFormData(prev => ({...prev, sort_order: parseInt(e.target.value) || 0}))} className="mt-1" />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>{language === 'pt' ? 'Mostrar no Ecrã Principal' : 'Show on Home Screen'}</Label>
                    <Switch checked={formData.show_on_home} onCheckedChange={(c) => setFormData(prev => ({...prev, show_on_home: c}))} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>{language === 'pt' ? 'Disponível para Pedidos' : 'Available for Orders'}</Label>
                    <Switch checked={formData.is_available} onCheckedChange={(c) => setFormData(prev => ({...prev, is_available: c}))} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>{language === 'pt' ? 'Ativo' : 'Active'}</Label>
                    <Switch checked={formData.is_active} onCheckedChange={(c) => setFormData(prev => ({...prev, is_active: c}))} />
                  </div>
                </div>
                <Button onClick={handleSave} className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full" data-testid="save-service-btn">
                  <Save size={16} className="mr-2" />
                  {language === 'pt' ? 'Guardar' : 'Save'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-[#9FA2B4] text-sm mb-4">
          {language === 'pt' 
            ? 'Configure os serviços que aparecem no ecrã principal e defina sua disponibilidade.'
            : 'Configure services shown on home screen and set their availability.'}
        </p>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#FF6B35]" /></div>
        ) : (
          <div className="space-y-3">
            {services.map((service) => (
              <div 
                key={service.category_id}
                className={`flex items-center justify-between p-3 rounded-xl border ${
                  service.is_active && service.is_available ? 'bg-white border-[#004E64]/10' : 'bg-gray-50 border-gray-200'
                }`}
                data-testid={`service-item-${service.category_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: service.color + '20' }}>
                    {service.image ? (
                      <img src={service.image.startsWith('http') ? service.image : `${API_URL}${service.image}`} alt={service.name} className="w-10 h-10 object-cover rounded-lg" />
                    ) : (
                      <span style={{ color: service.color }} className="text-xl">
                        {service.icon === 'Utensils' && '🍴'}
                        {service.icon === 'Wind' && '💨'}
                        {service.icon === 'ShoppingCart' && '🛒'}
                        {service.icon === 'Snowflake' && '❄️'}
                        {service.icon === 'Pill' && '💊'}
                        {service.icon === 'Coffee' && '☕'}
                        {!['Utensils', 'Wind', 'ShoppingCart', 'Snowflake', 'Pill', 'Coffee'].includes(service.icon) && '📦'}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-[#004E64]">
                      {language === 'pt' ? (service.name_pt || service.name) : service.name}
                    </p>
                    <p className="text-xs text-[#9FA2B4]">{service.route}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {service.show_on_home && (
                    <Badge className="bg-blue-100 text-blue-700 text-xs">Home</Badge>
                  )}
                  {!service.is_available && (
                    <Badge className="bg-red-100 text-red-700 text-xs">{language === 'pt' ? 'Indisponível' : 'Unavailable'}</Badge>
                  )}
                  {!service.is_active && (
                    <Badge className="bg-gray-100 text-gray-600 text-xs">{language === 'pt' ? 'Inativo' : 'Inactive'}</Badge>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openProductManager(service)}
                    className="text-xs border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35]/10 rounded-full px-3"
                    data-testid={`manage-menu-${service.category_id}`}
                  >
                    <ListOrdered size={14} className="mr-1" />
                    {language === 'pt' ? 'Menu' : 'Menu'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleVisibility(service, 'show_on_home')} title={language === 'pt' ? 'Mostrar/Ocultar no Home' : 'Show/Hide on Home'}>
                    {service.show_on_home ? <Eye size={14} /> : <EyeOff size={14} />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(service)} data-testid={`edit-service-${service.category_id}`}>
                    <Edit size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(service.category_id)} className="text-red-500 hover:text-red-700">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ServiceManagement;
